import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAllProjectsQuery,
  useCompaniesQuery,
  useAllInstallationsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useProjectFilesQuery,
  useUploadProjectFileMutation,
  useDeleteFileMutation,
  useUsersQuery,
  useUpdateUserMutation,
  useCreateInstallationMutation,
  useUpdateInstallationMutation,
  useDeleteInstallationMutation,
  useLatestTimesQuery,
  useAlertSummaryQuery,
} from 'src/queries';
import { useModal } from 'src/hooks/useModal';
import type { Project, Company, Installation } from 'src/types';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import EntityModal from '../components/EntityModal';
import ConfirmDialog from '../components/ConfirmDialog';
import FileManager from '../components/FileManager';
import ProjectForm from '../forms/ProjectForm';
import InstallationForm from '../forms/InstallationForm';
import ProjectExpandedRow from '../components/ProjectExpandedRow';
import ProjectUserAccess from '../components/ProjectUserAccess';

const ProjectsTab = () => {
  const { t } = useTranslation();
  const { data: projects = [], isLoading } = useAllProjectsQuery();
  const { data: companies = [] } = useCompaniesQuery();
  const { data: installations = [] } = useAllInstallationsQuery();
  const { data: users = [] } = useUsersQuery();
  const { data: latestTimes = {} } = useLatestTimesQuery();
  const { data: alertSummary = {} } = useAlertSummaryQuery();

  const createMutation = useCreateProjectMutation();
  const updateMutation = useUpdateProjectMutation();
  const deleteMutation = useDeleteProjectMutation();
  const updateUserMutation = useUpdateUserMutation();

  const createInstallationMutation = useCreateInstallationMutation();
  const updateInstallationMutation = useUpdateInstallationMutation();
  const deleteInstallationMutation = useDeleteInstallationMutation();

  const formModal = useModal<Project>();
  const deleteModal = useModal<Project>();
  const installationFormModal = useModal<Installation>();
  const deleteInstallationModal = useModal<Installation>();

  // When creating a new installation from inside a project's expanded row, this
  // holds the project to link the new installation to on success.
  const [createForProjectId, setCreateForProjectId] = useState<string | null>(null);

  // File management for the currently editing project
  const editingProjectId = formModal.editingItem?.id ?? null;
  const { data: projectFiles = [], isLoading: filesLoading } =
    useProjectFilesQuery(editingProjectId);
  const uploadMutation = useUploadProjectFileMutation(editingProjectId);
  const deleteFileMutation = useDeleteFileMutation();

  const companyMap = new Map<string, Company>(companies.map((c) => [c.id, c]));
  const installationMap = new Map<string, Installation>(installations.map((i) => [i.id, i]));

  const columns: Column<Project>[] = [
    { key: 'name', header: t('management.project.name') },
    { key: 'address', header: t('management.project.address') },
    {
      key: 'company_id',
      header: t('management.project.company'),
      render: (p) => companyMap.get(p.company_id ?? '')?.name ?? '-',
    },
    {
      key: 'installation_ids',
      header: t('management.project.installations'),
      render: (p) => String(p.installation_ids?.length ?? 0),
    },
  ];

  const updateAdminsWithNewProject = async (newProjectId: string) => {
    const admins = users.filter((u) => u.is_admin);
    const updates = admins
      .filter((admin) => !(admin.project_ids ?? []).includes(newProjectId))
      .map((admin) =>
        updateUserMutation.mutateAsync({
          id: admin.id,
          data: { project_ids: [...(admin.project_ids ?? []), newProjectId] },
        })
      );
    await Promise.all(updates);
  };

  const handleCreate = (data: Partial<Project>) => {
    createMutation.mutate(data, {
      onSuccess: (newProject) => {
        formModal.close();
        if (newProject?.id) {
          void updateAdminsWithNewProject(newProject.id);
        }
      },
    });
  };

  const handleUpdate = (data: Partial<Project>) => {
    if (!formModal.editingItem) return;
    updateMutation.mutate(
      { id: formModal.editingItem.id, data },
      { onSuccess: () => formModal.close() }
    );
  };

  const handleDelete = () => {
    if (!deleteModal.editingItem) return;
    deleteMutation.mutate(deleteModal.editingItem.id, {
      onSuccess: () => deleteModal.close(),
    });
  };

  // Grant/revoke a user's access to a project by rewriting that user's
  // project_ids (access is modeled on the user side, mirroring how admins are
  // auto-granted on project create).
  const handleToggleUserAccess = (userId: string, hasAccess: boolean) => {
    const user = users.find((u) => u.id === userId);
    if (!user || !formModal.editingItem) return;
    const projectId = formModal.editingItem.id;
    const current: string[] = user.project_ids ?? [];
    const next = hasAccess ? [...current, projectId] : current.filter((id) => id !== projectId);
    updateUserMutation.mutate({ id: userId, data: { project_ids: next } });
  };

  // ── Installation linking helpers ──────────────────────────────────────────
  // A project's installations are its `installation_ids`. Add/remove = rewrite
  // that array on the project (not a field on the installation).
  const setProjectInstallationIds = (project: Project, ids: string[]) =>
    updateMutation.mutate({ id: project.id, data: { installation_ids: ids } });

  const handleAttachInstallation = (project: Project, installationId: string) => {
    const current = project.installation_ids ?? [];
    if (current.includes(installationId)) return;
    setProjectInstallationIds(project, [...current, installationId]);
  };

  const handleRemoveFromProject = (project: Project, inst: Installation) => {
    const current = project.installation_ids ?? [];
    setProjectInstallationIds(
      project,
      current.filter((id) => id !== inst.id)
    );
  };

  const handleCreateInstallationForProject = (project: Project) => {
    setCreateForProjectId(project.id);
    installationFormModal.openForCreate();
  };

  const handleInstallationSubmit = (data: Partial<Installation>) => {
    if (installationFormModal.editingItem) {
      // Editing an existing installation.
      updateInstallationMutation.mutate(
        { id: installationFormModal.editingItem.id, data },
        { onSuccess: () => installationFormModal.close() }
      );
      return;
    }
    // Creating a new installation, then linking it to the originating project.
    createInstallationMutation.mutate(data, {
      onSuccess: (newInstallation) => {
        installationFormModal.close();
        const project = projects.find((p) => p.id === createForProjectId);
        if (project && newInstallation?.id) {
          setProjectInstallationIds(project, [
            ...(project.installation_ids ?? []),
            newInstallation.id,
          ]);
        }
        setCreateForProjectId(null);
      },
    });
  };

  const handleDeleteInstallation = () => {
    if (!deleteInstallationModal.editingItem) return;
    deleteInstallationMutation.mutate(deleteInstallationModal.editingItem.id, {
      onSuccess: () => deleteInstallationModal.close(),
    });
  };

  const isEditing = !!formModal.editingItem;
  const isEditingInstallation = !!installationFormModal.editingItem;

  return (
    <>
      <DataTable
        columns={columns}
        data={projects}
        isLoading={isLoading}
        onCreate={formModal.openForCreate}
        onEdit={(project) => formModal.openForEdit(project)}
        onDelete={(project) => deleteModal.openForEdit(project)}
        createLabel={t('management.projects')}
        renderExpanded={(project) => {
          const projInstallationIds = new Set(project.installation_ids ?? []);
          const projInstallations = (project.installation_ids ?? [])
            .map((id) => installationMap.get(id))
            .filter((i): i is Installation => !!i);
          const usersWithAccess = users.filter((u) => (u.project_ids ?? []).includes(project.id));
          const attachable = installations.filter((i) => !projInstallationIds.has(i.id));
          return (
            <ProjectExpandedRow
              installations={projInstallations}
              usersWithAccess={usersWithAccess}
              attachableInstallations={attachable}
              latestTimes={latestTimes}
              alertSummary={alertSummary}
              onCreateInstallation={() => handleCreateInstallationForProject(project)}
              onEditInstallation={(inst) => installationFormModal.openForEdit(inst)}
              onRemoveFromProject={(inst) => handleRemoveFromProject(project, inst)}
              onDeleteInstallation={(inst) => deleteInstallationModal.openForEdit(inst)}
              onAttachInstallation={(id) => handleAttachInstallation(project, id)}
            />
          );
        }}
      />

      <EntityModal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={isEditing ? t('management.edit') : t('management.create')}
        width="640px"
      >
        <ProjectForm
          initialData={formModal.editingItem ?? undefined}
          companies={companies}
          installations={installations}
          onSubmit={isEditing ? handleUpdate : handleCreate}
          isLoading={isEditing ? updateMutation.isPending : createMutation.isPending}
          onCancel={formModal.close}
        />
        {isEditing && (
          <FileManager
            files={projectFiles}
            isLoading={filesLoading}
            onUpload={(file) => uploadMutation.mutate(file)}
            onDelete={(fileId) => deleteFileMutation.mutate(fileId)}
            isUploading={uploadMutation.isPending}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
            label={t('management.project.documents')}
          />
        )}
        {isEditing && formModal.editingItem && (
          <ProjectUserAccess
            users={users}
            accessUserIds={users
              .filter((u) => (u.project_ids ?? []).includes(formModal.editingItem!.id))
              .map((u) => u.id)}
            onToggle={handleToggleUserAccess}
            isSaving={updateUserMutation.isPending}
          />
        )}
      </EntityModal>

      {/* Installation create/edit modal (used from the expanded project rows) */}
      <EntityModal
        isOpen={installationFormModal.isOpen}
        onClose={() => {
          installationFormModal.close();
          setCreateForProjectId(null);
        }}
        title={isEditingInstallation ? t('management.edit') : t('management.create')}
        width="640px"
      >
        <InstallationForm
          initialData={installationFormModal.editingItem ?? undefined}
          companies={companies}
          onSubmit={handleInstallationSubmit}
          isLoading={
            isEditingInstallation
              ? updateInstallationMutation.isPending
              : createInstallationMutation.isPending
          }
          onCancel={() => {
            installationFormModal.close();
            setCreateForProjectId(null);
          }}
        />
      </EntityModal>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onConfirm={handleDelete}
        onCancel={deleteModal.close}
        title={t('management.confirmDeleteTitle')}
        message={t('management.confirmDeleteMessage', {
          name: deleteModal.editingItem?.name ?? '',
        })}
        isLoading={deleteMutation.isPending}
      />

      <ConfirmDialog
        isOpen={deleteInstallationModal.isOpen}
        onConfirm={handleDeleteInstallation}
        onCancel={deleteInstallationModal.close}
        title={t('management.confirmDeleteTitle')}
        message={t('management.confirmDeleteMessage', {
          name: deleteInstallationModal.editingItem?.name ?? '',
        })}
        isLoading={deleteInstallationMutation.isPending}
      />
    </>
  );
};

export default ProjectsTab;
